import React, { useState } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { QuestionAnswer, QuestionOption, QuestionPayload } from 'components/ExportDesign/exportDesignQuestionData';

// A question the classification stops to ask before its result is final (UX S12 R-12, board 13). The
// Export Workflow's question card, unchanged: the same `.export-followup-*` markup and classes from the
// shared standard-workflow.css (FollowupQuestionCard.tsx), so a question looks the same in both tools. One
// card per turn, tagged "1 of 2". A click sends the option's label; "I don't know" is always offered.

export interface ExportDesignQuestionCardProps {
  question: QuestionPayload;
  /** The latest message and nothing running: the card can be answered. */
  active: boolean;
  /** How it was answered, once a later message answered it. */
  answer?: QuestionAnswer;
  disabled?: boolean;
  onAnswer: (value: string) => void;
}

const ExportDesignQuestionCard: React.FC<ExportDesignQuestionCardProps> = ({
  question,
  active,
  answer,
  disabled,
  onAnswer,
}) => {
  const [value, setValue] = useState('');
  const open = active && !answer;
  const locked = !open || Boolean(disabled);
  const inputId = `export-design-question-${question.id}`;
  const options = question.input ? [] : [...question.options, question.dontKnow];
  // As the Export Workflow: 2 or 4 options -> two columns, 3 -> three.
  const columns = options.length === 2 || options.length === 4 ? 2 : 3;

  const optionClass = (option: QuestionOption, base: string): string => {
    const chosen = answer?.optionId === option.id;
    return `${base}${chosen ? ' is-selected' : ''}${!open && !chosen ? ' is-muted' : ''}`;
  };

  return (
    <div className={`export-followup-card${open ? '' : ' is-answered'}`}>
      <div className='export-followup-head'>
        <div className='export-followup-heading'>
          <div className='export-followup-title'>
            {question.title}
            {question.total > 1 && (
              <span className='export-followup-badge'>{`${question.index + 1} of ${question.total}`}</span>
            )}
          </div>
          {question.context && <div className='export-followup-context'>{question.context}</div>}
        </div>
        {open && (
          <span className='export-followup-required'>
            <ErrorOutlineIcon aria-hidden='true' />
            Answer to continue
          </span>
        )}
      </div>

      {question.input && (
        <form
          className='export-followup-input'
          onSubmit={(event) => {
            event.preventDefault();
            const typed = value.trim();
            if (!typed || locked) return;
            onAnswer(typed);
          }}
        >
          <label className='export-followup-input-label' htmlFor={inputId}>
            {question.input.label}
          </label>
          <div className='export-followup-input-control'>
            <input
              id={inputId}
              type='text'
              value={open ? value : answer && !answer.optionId ? answer.text : ''}
              onChange={(event) => setValue(event.target.value)}
              placeholder={question.input.placeholder}
              disabled={locked}
            />
            <button type='submit' className='standard-workflow-primary-btn' disabled={locked || !value.trim()}>
              {question.input.submitLabel}
            </button>
          </div>
        </form>
      )}

      {options.length > 0 && (
        <div className={`export-followup-options export-followup-options-${columns}`} role='group' aria-label={question.title}>
          {options.map((option) => (
            <button
              key={option.id}
              type='button'
              className={optionClass(option, 'export-followup-option')}
              onClick={() => onAnswer(option.value)}
              disabled={locked}
              aria-pressed={answer?.optionId === option.id}
            >
              <span className='export-followup-option-label'>{option.label}</span>
              {option.description && <span className='export-followup-option-desc'>{option.description}</span>}
            </button>
          ))}
        </div>
      )}

      {question.input && (
        <div className='export-followup-alternate-actions'>
          <div className='export-followup-alternate-action'>
            <button
              type='button'
              className={optionClass(question.dontKnow, 'standard-workflow-secondary-btn export-followup-alternate-btn')}
              onClick={() => onAnswer(question.dontKnow.value)}
              disabled={locked}
            >
              {question.dontKnow.label}
            </button>
            {question.dontKnow.description && (
              <span className='export-followup-option-desc'>{question.dontKnow.description}</span>
            )}
          </div>
        </div>
      )}

      {open && !question.input && <div className='export-followup-note'>Or type your answer in the box below.</div>}
      {answer && !answer.optionId && !question.input && (
        <div className='export-followup-note'>{`Your answer: ${answer.text}`}</div>
      )}
    </div>
  );
};

export default ExportDesignQuestionCard;
